import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../Button'
import { Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '../Table'
import { MARK_STYLE, formatPoints, useMarkGlyph } from './marks'
import { markPoints, pointsOver, type ScorecardClimber, type ScorecardRoute } from './types'

const CLIMBER = '__climber'
const TOTAL = '__total'

interface Props {
  scoringType: string
  routes: ScorecardRoute[]
  climbers: ScorecardClimber[]
  /** A round is selected, so the total column also shows that round's points. */
  showRoundTotal: boolean
  myUserId?: string
  focus: { userId: string; tick: number } | null
  /** The first route of each round, which gets a divider on its left. */
  groupStarts: Set<string | undefined>
  renderClimber: (climber: ScorecardClimber) => ReactNode
  onOpenClimber?: (climber: ScorecardClimber) => void
  onOpenRoute: (routeId: string) => void
}

// Sticky cells need their own background or the scrolled columns show through.
const PINNED = 'sticky z-10 bg-content1 group-data-[odd=true]:bg-content2'

export function ScorecardMatrix({
  scoringType,
  routes,
  climbers,
  showRoundTotal,
  myUserId,
  focus,
  groupStarts,
  renderClimber,
  onOpenClimber,
  onOpenRoute,
}: Props) {
  const { t } = useTranslation()
  const glyph = useMarkGlyph()

  useEffect(() => {
    if (!focus) return
    document
      .getElementById(`scorecard-row-${focus.userId}`)
      ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  }, [focus])

  const columns: { key: string; route?: ScorecardRoute }[] = [
    { key: CLIMBER },
    ...routes.map((r) => ({ key: r.routeId, route: r })),
    { key: TOTAL },
  ]

  return (
    <>
      <Table
        aria-label={t('scorecard.tableLabel')}
        isHeaderSticky
        isStriped
        removeWrapper={false}
        classNames={{
          base: 'max-h-[70vh] overflow-auto rounded-2xl border border-divider',
          wrapper: 'p-0 shadow-none rounded-none',
          table: 'min-w-max',
          th: 'px-1 py-2 align-bottom',
          td: 'px-1 py-1.5',
          tr: 'group',
        }}
      >
        <TableHeader columns={columns}>
          {(col) =>
            col.key === CLIMBER ? (
              <TableColumn
                key={CLIMBER}
                className={`${PINNED} left-0 z-30 min-w-56 bg-content2 pl-3 text-left`}
              >
                {t('scorecard.colClimber')}
              </TableColumn>
            ) : col.key === TOTAL ? (
              <TableColumn
                key={TOTAL}
                className={`${PINNED} right-0 z-30 min-w-28 bg-content2 pr-3 text-right`}
              >
                {t('scorecard.colPoints')}
              </TableColumn>
            ) : (
              <TableColumn
                key={col.key}
                className={`w-12 min-w-12 text-center ${groupStarts.has(col.key) ? 'border-l-2 border-divider' : ''}`}
              >
                <RouteHeader route={col.route!} scoringType={scoringType} onOpen={onOpenRoute} />
              </TableColumn>
            )
          }
        </TableHeader>
        <TableBody items={climbers}>
          {(climber) => (
            <TableRow
              key={climber.userId}
              id={`scorecard-row-${climber.userId}`}
              className={climber.userId === myUserId ? '[&>td]:!bg-primary-50' : ''}
            >
              {(columnKey) => {
                if (columnKey === CLIMBER) {
                  const identity = (
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span
                        className={`w-6 shrink-0 text-center font-bold tabular-nums ${
                          climber.rank === 1 ? 'text-primary' : ''
                        }`}
                      >
                        {climber.rank}
                      </span>
                      <span className="min-w-0">{renderClimber(climber)}</span>
                    </span>
                  )
                  return (
                    <TableCell className={`${PINNED} left-0 pl-3`}>
                      {onOpenClimber ? (
                        <Button
                          variant="light"
                          disableRipple
                          title={t('scorecard.viewProfile')}
                          onPress={() => onOpenClimber(climber)}
                          className="h-auto min-w-0 justify-start px-0 text-left data-[hover=true]:bg-transparent"
                        >
                          {identity}
                        </Button>
                      ) : (
                        identity
                      )}
                    </TableCell>
                  )
                }
                if (columnKey === TOTAL) {
                  return (
                    <TableCell className={`${PINNED} right-0 pr-3 text-right tabular-nums`}>
                      <span className="block font-bold">{formatPoints(climber.totalScore)}</span>
                      {showRoundTotal && (
                        <span className="block text-[11px] text-foreground/55">
                          {formatPoints(pointsOver(routes, climber))}
                        </span>
                      )}
                    </TableCell>
                  )
                }
                const route = routes.find((r) => r.routeId === columnKey)!
                const mark = climber.marks[route.routeId]
                const label = mark
                  ? t(mark === 'flash' ? 'scorecard.markFlash' : 'scorecard.markTop', {
                      label: route.label,
                      points: formatPoints(markPoints(route, mark)),
                    })
                  : t('scorecard.markNone', { label: route.label })
                return (
                  <TableCell
                    className={`text-center ${groupStarts.has(route.routeId) ? 'border-l-2 border-divider' : ''}`}
                  >
                    <span
                      title={label}
                      aria-label={label}
                      className="inline-grid size-7 place-items-center"
                    >
                      {mark ? (
                        <span
                          className={`inline-grid size-7 place-items-center rounded-lg text-xs font-bold ${MARK_STYLE[mark]}`}
                          aria-hidden="true"
                        >
                          {glyph(mark)}
                        </span>
                      ) : (
                        <span
                          className="inline-block size-7 rounded-lg bg-content2"
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  </TableCell>
                )
              }}
            </TableRow>
          )}
        </TableBody>
      </Table>
      <p className="px-1 text-xs text-foreground/55">
        {t(scoringType === 'tops_count' ? 'scorecard.tableHintTops' : 'scorecard.tableHint')}
      </p>
    </>
  )
}

/** Number and hold colour, then how many topped it and what it is worth. */
function RouteHeader({
  route,
  scoringType,
  onOpen,
}: {
  route: ScorecardRoute
  scoringType: string
  onOpen: (routeId: string) => void
}) {
  const { t } = useTranslation()
  return (
    <Button
      variant="light"
      size="sm"
      disableRipple
      onPress={() => onOpen(route.routeId)}
      aria-label={t('scorecard.routeLabel', { label: route.label })}
      className="h-auto w-full min-w-0 flex-col gap-0.5 px-0 py-1"
    >
      <span className="flex items-center gap-1 text-sm font-bold text-foreground">
        {route.color && (
          <span
            className="size-2 rounded-full ring-1 ring-black/15"
            style={{ backgroundColor: route.color }}
            aria-hidden="true"
          />
        )}
        {route.label}
      </span>
      <span className="text-[10px] font-normal leading-tight text-foreground/55">
        {route.tops}×
      </span>
      {scoringType === 'thousand_split' && (
        <span className="text-[10px] font-normal leading-tight text-foreground/55">
          {Math.round(route.topPoints)}/{Math.round(route.flashPoints)}
        </span>
      )}
    </Button>
  )
}
